import React, { useState } from "react";
import {
  Alert,
  Typography,
  Stack,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";

import { useAddSubstituteAttendee, useRequestSubstituteLink } from "../../../store";
import MeetingErrorPanel from "./meeting-error-panel";
import MeetingSubstituteResultDisplay from "./meeting-substitute-result-display";
import MeetingSubstituteInviteForm from "./meeting-substitute-invite-form";
import { JoinCodeStatus, SubstituteResult, CanSubstituteData } from "../../../types";

const MeetingSubstituteInvitePanel: React.FC<{
  joinCode: string;
  userId: string;
  meetingId: string;
  canSubstituteData: CanSubstituteData;
}> = ({
  joinCode, userId, meetingId, canSubstituteData }) => {

    const [submitting, setSubmitting] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>("");

    const [results, setResults] = useState<SubstituteResult[] | null>(null);
    const [requested, setRequested] = useState(false);

    const onAddSuccess = (_message: string, result: SubstituteResult[]) => {
      setSubmitting(false);
      setResults(result);
    };

    const onAddError = (error: any) => {
      console.error("Error updating meeting:", error);

      setErrorMessage("Failed to add substitute attendee" +
        (error?.message ? ": " + error?.message : ""));

      setSubmitting(false);
    };

    const { mutate: addSubstituteAttendee } = useAddSubstituteAttendee(
      onAddSuccess,
      onAddError,
    );

    const onRequestSuccess = () => {
      setRequested(true);
      setSubmitting(false);
    };

    const onRequestError = (error: any) => {
      console.error("Failed to send request email", error)
      console.log(error);

      setErrorMessage((error?.message || "Error occurred"));

      setSubmitting(false);
    };

    const { mutate: requestLink } = useRequestSubstituteLink(
      onRequestSuccess,
      onRequestError,
    );

    const submitEmails = (emails: string[]) => {
      if (submitting) {
        return;
      }

      setErrorMessage("");
      addSubstituteAttendee({
        meetingId: meetingId || "",
        userId,
        joinCode,
        emails,
      });

      setSubmitting(true);
    };

    const onRequestNewLink = () => {
      if (submitting) {
        return;
      }

      setErrorMessage("");
      requestLink({
        meetingId: meetingId || "",
        userId,
        joinCode,
      });

      setSubmitting(true);
    };

    const linkUsed = canSubstituteData.joinCodeStatus === JoinCodeStatus.canRequestNewCode || canSubstituteData.joinCodeStatus === JoinCodeStatus.waitForJoinCode;
    const waitForLink = canSubstituteData.joinCodeStatus === JoinCodeStatus.waitForJoinCode;

    if (!linkUsed && canSubstituteData.joinCodeStatus !== JoinCodeStatus.valid && canSubstituteData.joinCodeStatus !== JoinCodeStatus.usingAuth) {
      return <MeetingErrorPanel message={canSubstituteData.message || "Invalid join code"} />;
    }

    return (
      <Paper
        elevation={20}
        sx={{
          width: 800,
          p: 2,
          alignItems: "center",
          borderRadius: 2,
        }}
      >
        <Stack spacing={2} alignItems="center" width="100%">
          <Typography variant="h4" component="h1" textAlign="left">
            Invite Replacements
          </Typography>

          <Stack spacing={1}>
            <Typography variant="body1" textAlign="center">
              You can invite up to five existing users by entering their email
              addresses.
            </Typography>
            <Typography variant="body1" textAlign="center">
              For security reasons once you have submitted this form you need to
              request a new link to add additional emails.
            </Typography>
          </Stack>

          {results ? (
            <MeetingSubstituteResultDisplay results={results} />
          ) : requested ? (
            <Stack sx={{ width: "100%" }}>
              <Alert severity="success">Invite link has been requested.</Alert>
            </Stack>
          ) : errorMessage ? (
            <Stack sx={{ width: "100%" }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Stack>
          ) : waitForLink ? (
            <Stack sx={{ width: "100%" }}>
              <Alert severity="error">{canSubstituteData.message}</Alert>
            </Stack>
          ) : !linkUsed && !submitting ? (
            <MeetingSubstituteInviteForm submitEmails={submitEmails} domainWhiteList={canSubstituteData?.domainWhiteList || []} />
          ) : (
            <Button
              onClick={onRequestNewLink}
              variant="outlined"
              fullWidth
              disabled={submitting}
            >
              {submitting ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={24} />
                  <span>Requesting</span>
                </Stack>
              ) : (
                "Request new invite link"
              )}
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

export default MeetingSubstituteInvitePanel;
